import useDrivePicker from "../../lib/GoogleDrivePicker/drivePicker.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useGoogleLogin } from "@react-oauth/google";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useDispatch } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import { conversationActions } from "../../store/conversation.tsx";
import axios from "../../utils/axios.tsx";
import TallyButton from "../common/TallyButton.tsx";

const exampleGoodSpreadsheet: (string | number)[][] = [
  ["Account", "Month", "Category", "Amount"],
  [101, "Jan", "Sales", 100],
  [102, "Jan", "Marketing", 50],
  [103, "Feb", "Sales", 120],
  [104, "Feb", "Marketing", 60],
  [105, "Mar", "Sales", 130],
  [106, "Mar", "Marketing", 70],
];

const createSpreadsheetErrorMessageHTML = (
  fileName: string,
  errorMessage: string,
) => {
  const exampleTable = `
    <table class="tallyTable" style="border-collapse: collapse; width: 100%; margin-top: 8px; margin-bottom: 6px;">
      <thead>
        <tr>
          ${exampleGoodSpreadsheet[0].map((col) => `<th>${col}</th>`).join("")}
        </tr>
      </thead>
      <tbody>
        ${exampleGoodSpreadsheet
          .slice(1)
          .map(
            (row) =>
              `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`,
          )
          .join("")}
      </tbody>
    </table>
  `;

  return msg({
    context: "Custom message for spreadsheet connected",
    message: `
      <p>❌ <strong>Spreadsheet <em>${fileName}</em> Needs Correction</strong></p>
      <p>Your spreadsheet has some issues that need fixing before Tally can analyze it.</p>
      <p>Here’s what’s wrong: <em>${errorMessage}</em></p>
      <p>Example of a properly structured spreadsheet you can use for reference:</p>
      ${exampleTable}
      <p>Once corrected, you can continue asking questions about your data, and Tally will provide clear, accurate insights just like with any valid spreadsheet.</p>
    `,
  });
};

export type ConnectionFeedback =
  | {
      status: "idle";
      message: "";
    }
  | {
      status: "loading" | "success" | "error";
      message: string;
    };

interface GoogleSheetsConnectButtonProps {
  feedback: ConnectionFeedback;
  onFeedbackChange: (feedback: ConnectionFeedback) => void;
  onPickerOpen?: () => void;
  onPickerClose?: () => void;
}

const GoogleSheetsConnectButton: React.FC<GoogleSheetsConnectButtonProps> = ({
  feedback,
  onFeedbackChange,
  onPickerOpen,
  onPickerClose,
}) => {
  const [openPicker] = useDrivePicker();
  const dispatch = useDispatch();

  const handleOpenPicker = useGoogleLogin({
    flow: "auth-code",
    scope: "https://www.googleapis.com/auth/drive.file",
    onSuccess: async ({ code }: any) => {
      try {
        const res = await axios.post("/v1/exchange-google-code/", { code });
        const accessToken = res.data.access_token;

        onPickerOpen?.();

        openPicker({
          clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          developerKey: import.meta.env.VITE_GOOGLE_API_KEY,
          viewId: "SPREADSHEETS",
          token: accessToken,
          showUploadView: true,
          supportDrives: true,
          appId: import.meta.env.VITE_GOOGLE_APP_ID,
          viewMimeTypes: "application/vnd.google-apps.spreadsheet",
          callbackFunction: async (data: any) => {
            if (data.action === "cancel") {
              onPickerClose?.();
              return;
            }

            if (data.action !== "picked") {
              return;
            }

            const file = data.docs[0];
            const fileName = file.name;
            const fileId = file.id;

            onPickerClose?.();
            onFeedbackChange({
              status: "loading",
              message: i18n._(
                msg({
                  context:
                    "Status label shown while a spreadsheet is connecting",
                  message: "Connecting spreadsheet...",
                }),
              ),
            });

            try {
              const response = await axios.post(
                "/v1/integrations/sync/",
                JSON.stringify({
                  integration_identifier: "google_sheets",
                  secret: accessToken,
                  integration_parameters: { file_id: fileId },
                  eager: true,
                }),
              );

              let message;

              if (response.status !== 200) {
                const errorMessage =
                  response.statusText ||
                  i18n._(
                    msg({
                      context:
                        "Fallback error when spreadsheet connection response has no detail",
                      message: "Unknown error",
                    }),
                  );
                const rawMessage = createSpreadsheetErrorMessageHTML(
                  fileName,
                  errorMessage,
                );
                message = i18n._(rawMessage).replace(/\n\s+/g, " ").trim();
                onFeedbackChange({
                  status: "error",
                  message: `${i18n._(
                    msg({
                      context:
                        "Status label shown when spreadsheet connection fails",
                      message: "Spreadsheet connection failed:",
                    }),
                  )} ${errorMessage}`,
                });
              } else {
                const rawMessage = msg({
                  context: "Custom message for spreadsheet connected",
                  message: `
                    <p>✅ <strong>Spreadsheet <em>${fileName}</em> Connected Successfully</strong></p>
                    <p>Your Google Spreadsheet has been linked. You can now start asking questions about the data it contains — 
                    for example, <em>"What were my total sales last month according to ${fileName} spreadsheet?"</em> or 
                    <em>"Show trends in expenses by category from ${fileName} sheet."</em></p>
                    <p>Tally will analyze the spreadsheet and give you clear, accurate insights.</p>
                    <p>If you update the spreadsheet, just ask again — Tally always works with the latest version.</p>
                  `,
                });

                message = i18n._(rawMessage).replace(/\n\s+/g, " ").trim();
                onFeedbackChange({
                  status: "success",
                  message: i18n._(
                    msg({
                      context:
                        "Status label shown when spreadsheet connection succeeds",
                      message: "Spreadsheet connected successfully.",
                    }),
                  ),
                });
              }

              dispatch(
                conversationActions.appendCustomActionResponse({
                  text: message,
                }),
              );
            } catch (error: any) {
              const errorMessage =
                error.response?.data?.detail ||
                error.message ||
                i18n._(
                  msg({
                    context:
                      "Fallback error when spreadsheet connection response has no detail",
                    message: "Unknown error",
                  }),
                );

              onFeedbackChange({
                status: "error",
                message: `${i18n._(
                  msg({
                    context:
                      "Status label shown when spreadsheet connection fails",
                    message: "Spreadsheet connection failed:",
                  }),
                )} ${errorMessage}`,
              });

              dispatch(
                conversationActions.appendCustomActionResponse({
                  text: errorMessage,
                }),
              );
            }
          },
        });
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail ||
          error.message ||
          i18n._(
            msg({
              context:
                "Fallback error when spreadsheet connection response has no detail",
              message: "Unknown error",
            }),
          );

        onFeedbackChange({
          status: "error",
          message: `${i18n._(
            msg({
              context: "Status label shown when spreadsheet connection fails",
              message: "Spreadsheet connection failed:",
            }),
          )} ${errorMessage}`,
        });
      }
    },
    onError: () => {
      const errorMessage = i18n._(
        msg({
          context: "Error shown when Google login fails for spreadsheet picker",
          message: "Google login failed.",
        }),
      );
      onFeedbackChange({
        status: "error",
        message: errorMessage,
      });
      console.error("Google Login Failed");
    },
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <TallyButton
        onClick={() => {
          onFeedbackChange({ status: "idle", message: "" });
          handleOpenPicker();
        }}
        startIcon={<AddCircleOutlineIcon />}
        disabled={feedback.status === "loading"}
      >
        <Trans context="Label for button to connect a new spreadsheet">
          Connect new spreadsheet
        </Trans>
      </TallyButton>

      {feedback.status !== "idle" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: "12px",
            backgroundColor:
              feedback.status === "success"
                ? "rgba(46, 125, 50, 0.1)"
                : feedback.status === "error"
                  ? "rgba(211, 47, 47, 0.08)"
                  : "rgba(0, 0, 0, 0.04)",
          }}
        >
          {feedback.status === "loading" ? (
            <CircularProgress size={18} />
          ) : feedback.status === "success" ? (
            <CheckCircleOutlineIcon sx={{ color: "#2e7d32" }} />
          ) : (
            <HighlightOffIcon sx={{ color: "#d32f2f" }} />
          )}
          <Typography
            variant="body2"
            color={
              feedback.status === "success"
                ? "#2e7d32"
                : feedback.status === "error"
                  ? "#d32f2f"
                  : "text.secondary"
            }
          >
            {feedback.message}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GoogleSheetsConnectButton;
